import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { useAuth } from '../../hooks/useAuth';
import { InviteHeader, InviteShell, SignInLink } from './AcceptInviteLayout';
import { SetupProgramForm } from './SetupProgramForm';
import { useSetupInvitationPreview } from './useSetupInvitationPreview';
import type { SetupProgramOutcome } from './useSetupInvitationPreview';

/**
 * The public `/setup-program` page: a platform admin invited this person to create a new program.
 * They set up their account and the organization, and become its admin.
 *
 * Modelled on `AcceptInvite`, and for the same reasons:
 *
 * - **The token arrives in the URL fragment (`#token=...`) and is cleared on sight.** A fragment is
 *   never sent in a `Referer` or reaches an access log. Read it with `window.location.hash`.
 * - **No session is issued.** The page ends on a hand-off to sign-in. When payment is required,
 *   `ProtectedRoute` sends the new admin to `/signup/payment` after they sign in.
 */

const readTokenFromHash = (): string | null => {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;

  const token = new URLSearchParams(hash).get('token')?.trim();
  return token || null;
};

const clearHash = () => {
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
};

export const SetupProgram = () => {
  const { user, logout } = useAuth();

  /** Read during the first render, before the effect below clears it. */
  const [token] = useState(readTokenFromHash);
  const [outcome, setOutcome] = useState<SetupProgramOutcome | null>(null);
  const { status, reload } = useSetupInvitationPreview(token);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    clearHash();
  }, []);

  /**
   * Sign out anyone already signed in, as AcceptInvite does: the success state hands off to
   * `/login`, which bounces a signed-in user into the app as the wrong person. Through AuthContext
   * so the per-tenant caches are cleared too. Once, on mount: auth has settled before any route
   * renders.
   */
  useEffect(() => {
    if (user) {
      void logout().catch(() => {
        // A failed sign-out must not take the invitation down with it.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const view = outcome ?? status;

  /** Each phase swaps the whole page, so focus moves to the new heading rather than to `<body>`. */
  useEffect(() => {
    if (view.phase !== 'loading') headingRef.current?.focus();
  }, [view.phase]);

  if (view.phase === 'loading') {
    return (
      <InviteShell>
        <div className="accept-invite-loading" role="status">
          <p>Checking your invitation...</p>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'invalid') {
    return (
      <InviteShell>
        <InviteHeader title="This invitation link no longer works" headingRef={headingRef} />
        <div className="accept-invite-message accept-invite-message--error">
          <p>This invitation is invalid or has expired.</p>
          <p>Ask whoever invited you to send a new one.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInLink>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'unavailable') {
    return (
      <InviteShell>
        <InviteHeader title="We could not check your invitation" headingRef={headingRef} />
        <div className="accept-invite-message accept-invite-message--error">
          <p>{view.message}</p>
        </div>
        <div className="accept-invite-actions">
          {view.canRetry && (
            <Button type="button" fullWidth onClick={() => void reload()} data-testid="retry-button">
              Try again
            </Button>
          )}
          <SignInLink>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'registered') {
    return (
      <InviteShell>
        <InviteHeader title="You already have an account" headingRef={headingRef} />
        <div className="accept-invite-message accept-invite-message--warning">
          <p>{view.message}</p>
          <p>Sign in with {view.email} instead.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInLink email={view.email}>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  if (view.phase === 'accepted') {
    const { accepted } = view;
    return (
      <InviteShell>
        <InviteHeader
          title="Your program is ready"
          subtitle={
            accepted.requiresPayment
              ? `Sign in to complete payment and activate ${accepted.organizationName}.`
              : `You are the admin of ${accepted.organizationName}.`
          }
          headingRef={headingRef}
        />
        <div className="accept-invite-message accept-invite-message--success">
          <p>Sign in with {accepted.email} and the password you just set.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInLink email={accepted.email}>Go to sign in</SignInLink>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      {token && (
        <SetupProgramForm
          token={token}
          preview={view.preview}
          headingRef={headingRef}
          onOutcome={setOutcome}
        />
      )}
    </InviteShell>
  );
};
